import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { ArrowBack as BackIcon, Download as DownloadIcon } from '@mui/icons-material';
import { api } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';

function ProgramEnrollments({ isEmbedded = false, programId: propProgramId, program: propProgram }) {
  const { id: routeId } = useParams();
  const id = propProgramId || routeId;
  const navigate = useNavigate();
  const [program, setProgram] = useState(propProgram || null);
  const [enrollments, setEnrollments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Fetch program details if not provided and enrollments
      const enrollmentData = await api.getProgramEnrollments(id);

      if (!propProgram) {
        const programData = await api.getProgram(id);
        setProgram(programData);
      }

      setEnrollments(enrollmentData.enrollments || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.error || 'Failed to load enrollments');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'completed':
        return 'info';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleExportCSV = () => {
    if (!program || enrollments.length === 0) {
      alert('No enrollments to export');
      return;
    }

    // Prepare CSV headers
    const headers = [
      'Enrollee Name',
      'Gender',
      'Age',
      'Enroller Name',
      'Enroller Email',
      'Enroller Phone',
      'Status',
      'Enrolled Date'
    ];

    // Prepare CSV rows
    const rows = enrollments.map(enrollment => {
      return [
        enrollment.enrollee_name || '',
        enrollment.enrollee_gender ? enrollment.enrollee_gender.charAt(0).toUpperCase() + enrollment.enrollee_gender.slice(1) : 'N/A',
        calculateAge(enrollment.enrollee_date_of_birth),
        enrollment.enroller_name || '',
        enrollment.enroller_email || '',
        enrollment.enroller_phone || '',
        enrollment.status || '',
        formatDate(enrollment.created_at)
      ];
    });

    // Convert to CSV format
    const csvContent = [
      headers.map(h => `"${h}"`).join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${program.name.replace(/[^a-z0-9]/gi, '_')}_enrollments_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading enrollments...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button onClick={fetchData} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  const content = (
    <>
      {!isEmbedded && (
        <>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Enrollments: {program?.name}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {program?.term} • {program?.location}
          </Typography>
        </>
      )}

      {enrollments.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No enrollments yet
            </Typography>
          </Paper>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total Enrollments: {enrollments.length}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
            </Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Student</strong></TableCell>
                    <TableCell><strong>Gender</strong></TableCell>
                    <TableCell><strong>Age</strong></TableCell>
                    <TableCell><strong>Parent</strong></TableCell>
                    <TableCell><strong>Contact</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Enrolled On</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {enrollments.map((enrollment) => (
                    <TableRow key={enrollment.id} hover>
                      <TableCell>
                        <Typography variant="body1">
                          {enrollment.enrollee_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {enrollment.enrollee_gender ? (
                          <Chip
                            label={enrollment.enrollee_gender.charAt(0).toUpperCase() + enrollment.enrollee_gender.slice(1)}
                            size="small"
                            variant="outlined"
                            color={enrollment.enrollee_gender === 'male' ? 'primary' : 'secondary'}
                          />
                        ) : 'N/A'}
                      </TableCell>
                      <TableCell>{calculateAge(enrollment.enrollee_date_of_birth)}</TableCell>
                      <TableCell>
                        {enrollment.enroller_name}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{enrollment.enroller_email}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {enrollment.enroller_phone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={enrollment.status}
                          color={getStatusColor(enrollment.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(enrollment.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
    </>
  );

  if (isEmbedded) {
    return content;
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
      <Container maxWidth="lg">
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/admin/programs')}
          sx={{ mb: 2 }}
        >
          Back to Programs
        </Button>
        {content}
      </Container>
    </Box>
  );
}

export default ProgramEnrollments;
